import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme, darkTheme } from '../AppGlobals';
import { Nav, INavLink } from '@fluentui/react/lib/Nav';
import { IEntity } from '../types';
import { OrbUserContext } from '../components/OrbUserContext';
import { Icon, IIconStyles } from '@fluentui/react/lib/Icon';
import { SprintFilterCategoryKeys } from './app03Workflows/WorkflowTasks';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';

interface IZedXMainMenu {
    onLinkClicked?: () => void;
    onLogout?: () => void;
    authType: string;
    menuData?: any[];
    lastFiveApps: IEntity[];
}

export const ZedXMainMenu = ({
    onLogout,
    authType,
    onLinkClicked,
    menuData,
    lastFiveApps
}: IZedXMainMenu): React.ReactElement => {
    const { pathname } = useLocation();
    const history = useHistory();
    const { userContext } = useContext(OrbUserContext);
    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;

    const [navs, setNavs] = useState<INavLink[]>([]);

    useEffect(() => {
        if (!(userContext?.UserId > 0)) return;

        const routerNav = (path: string): void => {
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        const links: INavLink[] = [{ name: 'Home', path: '/' }].map(NamePathToNavLink);

        const menuDataMap = new Map(menuData?.map(item => [item.ID, item]));

        const filteredMenuData =
            userContext.UserType === 'Student'
                ? menuData?.filter(x => x.ID === 13) // Only Study Planner
                : menuData?.filter(x => x.ID !== 13); // Exclude Study Planner

        const allAppsLinks = filteredMenuData.map(m => ({
            name: m.MenuText,
            key: `m_all_${m.ID}`,
            path: `${m.AppLink}/welcome`
        }));

        const recentAppsLinks = lastFiveApps
            .map(value => menuDataMap.get(value.ID))
            .filter(menuItem => menuItem && filteredMenuData.some(app => app.ID === menuItem.ID))
            .map(menuItem => ({
                name: menuItem.MenuText,
                key: `m_recent_${menuItem.ID}`,
                path: `${menuItem.AppLink}/welcome`
            }));

        links.push({
            key: 'recent_apps',
            name: 'Recent Apps',
            url: null,
            isExpanded: true,
            links: recentAppsLinks.map(NamePathToNavLink)
        });

        links.push({
            key: 'all_apps',
            name: 'All Apps',
            url: null,
            isExpanded: false,
            links: allAppsLinks.map(NamePathToNavLink)
        });

        links.push(NamePathToNavLink({ name: 'Account Admin', path: '/admin/welcome' }));

        if (authType === 'CustomJwt') {
            links.push(NamePathToNavLink({ name: 'Change Password', path: '/account/change-password' }));
        }

        links.push({
            name: 'Logout',
            className: 'logout-link',
            key: 'logout',
            url: '',
            onClick: () => onLogout?.()
        });

        setNavs(links);
    }, [history, lastFiveApps, onLinkClicked, userContext, menuData, authType, onLogout]);

    const selectedKey: string = pathname === '/' ? '/' : pathname;

    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: '0',
                        paddingBottom: '1px',
                        paddingLeft: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-ZedXMainMenu.png`}
                        alt="ZedX"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Navigation */}
                <Nav
                    groups={[{ links: navs }]}
                    selectedKey={selectedKey}
                    onRenderLink={(lnk) => _onRenderNavLink(lnk, 0, 0, 0, 0, true)}
                />
            </div>
        </ThemeProvider>
    );
};

export function _onRenderNavLink(link: INavLink, totalCount1?: number, totalCount2?: number, totalCount3?: number, totalCount4?: number, mainMenu?: boolean) {
    // Common styles
    const commonStyle = { paddingLeft: '10px' };
    const parentChildStyle = { marginLeft: '-1px' };
    const level2Style = { marginLeft: '-11px' };
    const iconStyle = { root: { fontSize: '13px', /*color: '#99D9EA',*/ marginRight: '5px' } };

    const filterIconStyle: IIconStyles = {
        root: {
            fontSize: '10px',
            marginRight: '5px',
            position: 'relative',
            top: '1px',
        }
    };

    const badgeStyle = {
        fontSize: '12px',
        backgroundColor: 'green',
        color: 'white',
        padding: '2px',
        paddingLeft: '4px',
        paddingRight: '22px',
        borderRadius: '4px'
    };

    // Common icon or image render function
    const renderIconOrImage = (iconName?: string, imgBGWithoutHash?: string) => {
        const color = imgBGWithoutHash ?? '99D9EA';

        return iconName
            ? <Icon iconName={iconName} styles={iconStyle} />
            : <img
                width='8'
                height='8'
                src={`https://placehold.co/10x10/${color}/${color}`}
            />;
    };


    if (mainMenu === true) {
        switch (link.key) {
            case '/':
                return (
                    <span>
                        {renderIconOrImage("Home")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );

            case 'recent_apps':
                return (
                    <span style={parentChildStyle}>
                        {renderIconOrImage("Recent")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            case 'all_apps':
                return (
                    <span style={parentChildStyle}>
                        {renderIconOrImage("AllApps")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            case '/admin/welcome':
                return (
                    <span>
                        {renderIconOrImage("Admin")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );

            case '/account/change-password':
                return (
                    <span>
                        {renderIconOrImage("PasswordField")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );

            case 'logout':
                return (
                    <span>
                        {renderIconOrImage("SignOut")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );


            default:
                if (link.key.endsWith('/welcome')) {
                    return (
                        <span style={level2Style}>
                            {renderIconOrImage("AppIconDefault")}
                            <span style={commonStyle}>{link.name}</span>
                        </span>
                    );
                }
                return <span>{link.name}</span>;
        }

    }

    // Render based on link key
    switch (link.key) {
        case SprintFilterCategoryKeys.Backlog:
            return (
                <span>
                    {renderIconOrImage(null, 'C3C3C3')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    {/* <span style={badgeStyle}>{0}</span> */}
                </span>
            );
        case SprintFilterCategoryKeys.CurrentSprint:
            return (
                <span>
                    {renderIconOrImage(null, '22B14C')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    {/* <span style={badgeStyle}>{0}</span> */}
                </span>
            );
        case 'Current Stages': //kanban
            return (
                <span>
                    {renderIconOrImage(null, '22B14C')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                </span>
            );
        case SprintFilterCategoryKeys.PlannedSprint:
            return (
                <span>
                    {renderIconOrImage(null, 'FF7F27')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    {/* <span style={badgeStyle}>{0}</span> */}
                </span>
            );
        case SprintFilterCategoryKeys.HistoricSprint:
            return (
                <span>
                    {renderIconOrImage(null, 'FF7F27')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    {/* <span style={badgeStyle}>{0}</span> */}
                </span>
            );
        case SprintFilterCategoryKeys.Epic:
            return (
                <span>
                    {renderIconOrImage(null, 'C3C3C3')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    {/* <span style={badgeStyle}>{0}</span> */}
                </span>
            );
        case SprintFilterCategoryKeys.Archived:
            return (
                <span>
                    {renderIconOrImage(null, 'C3C3C3')}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    {/* <span style={badgeStyle}>{0}</span> */}
                </span>
            );
        case '/a2/portfolios':
            return (
                <span>
                    {renderIconOrImage()}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount1}</span>
                </span>
            );

        case '/a2/programmes':
            return (
                <span>
                    {renderIconOrImage()}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount2}</span>
                </span>
            );

        case '/a2/projects':
            return (
                <span>
                    {renderIconOrImage()}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount3}</span>
                </span>
            );

        case '/':
            return (
                <span>
                    {renderIconOrImage("Home")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a4/welcome':
        case '/a5/welcome':
        case '/a6/welcome':
        case '/a8/welcome':
        case '/a1/welcome':
        case '/a10/welcome':
        case '/a13/welcome':
        case '/a9/welcome':
        case '/a7/welcome':
        case '/a12/welcome':
        case '/a11/welcome':
        case '/a3/welcome':
        case '/a2/welcome':
            return (
                <span>
                    {renderIconOrImage("WavingHand")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a4/insights':
        case '/a5/insights':
        case '/a6/insights':
        case '/a8/insights':
        case '/a1/insights':
        case '/a13/insights':
        case '/a10/insights':
        case '/a9/insights':
        case '/a7/insights':
        case '/a12/insights':
        case '/a11/insights':
        case '/a3/insights':
        case '/a2/insights':
            return (
                <span>
                    {renderIconOrImage("ViewDashboard")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a4/updates':
        case '/a5/updates':
        case '/a8/updates':
        case '/a6/manage-cases':
        case '/a1/workflows':
        case '/a10/workflows':
        case '/a13/tasks':
            return (
                <span>
                    {renderIconOrImage("ReportLibrary")}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount1}</span>
                </span>
            );

        case '/a1/cwf-portfolios':
        case '/a10/cwf-portfolios':
            return (
                <span>
                    {renderIconOrImage("DocLibrary")}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount2}</span>
                </span>
            );

        case '/a10/cwf-projects':
            return (
                <span>
                    {renderIconOrImage("ProjectCollection")}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount3}</span>
                </span>
            );

        case '/a10/sprints':
            return (
                <span>
                    {renderIconOrImage("Sprint")}
                    <span style={{ ...commonStyle, marginRight: '25px' }}>{link.name}</span>
                    <span style={badgeStyle}>{totalCount3}</span>
                </span>
            );

        case 'work-items-parent':
            return (
                <span>
                    {renderIconOrImage("Taskboard")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case '/a9/updates':
        case '/a7/updates':
            return (
                <span>
                    {renderIconOrImage("SpeedHigh")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case 'manage_risks':
        case 'manage_issues':
        case 'manage_incidents':
            return (
                <span style={parentChildStyle}>
                    {renderIconOrImage("ClipboardList")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case 'gw_settings':
        case 'as_settings':
        case 'risk_settings':
            return (
                <span style={parentChildStyle}>
                    {renderIconOrImage("Settings")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a3/risks':
            return (
                <span style={level2Style}>
                    {renderIconOrImage("SpeedHigh")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );
        case '/a3/risk-mitigating-actions':
            return (
                <span style={level2Style}>
                    {renderIconOrImage("Umbrella")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );
        case '/a3/issues':
            return (
                <span style={level2Style}>
                    {renderIconOrImage("IssueSolid")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case '/a3/incidents':
            return (
                <span style={level2Style}>
                    {renderIconOrImage("AlertSolid")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case '/a3/issues-actions':
        case '/a3/incidents-actions':
            return (
                <span style={level2Style}>
                    {renderIconOrImage("Repair")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case 'Management':
            return (
                <span>
                    {renderIconOrImage("ProjectCollection")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case 'PortfolioSettings':
        case 'RisksSettings':
            return (
                <span>
                    {renderIconOrImage("Settings")}
                    <span style={{ ...commonStyle }}>{link.name}</span>
                </span>
            );

        case '/a4/outputs':
        case '/a5/outputs':
        case '/a6/outputs':
        case '/a7/outputs':
        case '/a3/outputs':
            return (
                <span>
                    {renderIconOrImage("Send")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a4/properties':
        case '/a5/properties':
        case '/a6/properties':
        case '/a8/properties':
        case '/a9/properties':
        case '/a7/properties':
            return (
                <span>
                    {renderIconOrImage("Settings")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a2/settings/benefit-types':
        case '/a2/settings/customFields':
        case '/a2/settings/reporting-frequencies':
        case '/a2/settings/risk-types':
        case '/a2/settings/risk-discussion-forums':
        case '/a2/settings/units':
        case '/a2/settings/thresholds':
        case '/a2/settings/threshold-appetites':
        case '/a3/settings/customFields':
        case '/a3/settings/risk-types':

            return (
                <span>
                    {renderIconOrImage("CheckListText")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a3/settings/heatmap-config':
            return (
                <span>
                    {renderIconOrImage("ConfigurationSolid")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a2/visions':
            return (
                <span>
                    {renderIconOrImage("BullseyeTarget")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a2/objectives':
            return (
                <span>
                    {renderIconOrImage("Flag")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );

        case '/a1/settings/templates':
        case '/a10/settings/templates':
            return (
                <span>
                    {renderIconOrImage("ScopeTemplate")}
                    <span style={commonStyle}>{link.name}</span>
                </span>
            );
        default:
            //console.log('link.key', link.key);
            const normalizedKey = link.key
                ?.replace('/a10/', '/workflows/')
                ?.replace('/a1/', '/workflows/');

            //console.log('normalizedKey', normalizedKey);

            if (normalizedKey.startsWith('/workflows/cwf-projects-for-portfolio/')) {
                return (
                    <span>
                        {renderIconOrImage("ProjectCollection")}
                        <span style={{ ...commonStyle, marginRight: '25px' }}>
                            <span style={{ marginRight: '3px' }}>{link.name}</span>
                            <Icon iconName="Filter" styles={filterIconStyle} />
                        </span>
                    </span>
                );
            }

            else if (normalizedKey.startsWith('/workflows/workflows-for-portfolio/')) {
                return (
                    <span>
                        {renderIconOrImage("ReportLibrary")}
                        <span style={{ ...commonStyle, marginRight: '25px' }}>
                            <span style={{ marginRight: '3px' }}>{link.name}</span>
                            <Icon iconName="Filter" styles={filterIconStyle} />
                        </span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/workflows/workflows-for-project/')) {
                return (
                    <span>
                        {renderIconOrImage("ReportLibrary")}
                        <span style={{ ...commonStyle, marginRight: '25px' }}>
                            <span style={{ marginRight: '3px' }}>{link.name}</span>
                            <Icon iconName="Filter" styles={filterIconStyle} />
                        </span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/workflows/epics/')) {
                return (
                    <span>
                        {renderIconOrImage("Library")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/workflows/sprints/')) {
                return (
                    <span>
                        {renderIconOrImage("ProgressLoopOuter")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/workflows/workflow-users/')) {
                return (
                    <span>
                        {renderIconOrImage("People")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/workflows/settings/work-item-types/')) {
                return (
                    <span>
                        {renderIconOrImage("List")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a3/risk-mitigating-actions-for-risk/')) {
                return (
                    <span>
                        {renderIconOrImage("Umbrella")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a3/sign-off-for-risk/')) {
                return (
                    <span>
                        {renderIconOrImage("PostUpdate")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a3/issues-actions-for-issue/')) {
                return (
                    <span>
                        {renderIconOrImage("Repair")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a3/incidents-actions-for-incident/')) {
                return (
                    <span>
                        {renderIconOrImage("Repair")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/objectives-for-vision')) {
                return (
                    <span>
                        {renderIconOrImage("Flag")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/portfolios-for-objective/')) {
                return (
                    <span>
                        {renderIconOrImage("DocLibrary")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/programmes-for-portfolio/')) {
                return (
                    <span>
                        {renderIconOrImage("DocLibrary")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/projects-for-portfolio/')) {
                return (
                    <span>
                        {renderIconOrImage("ProjectCollection")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/projects-for-programme/')) {
                return (
                    <span>
                        {renderIconOrImage("ProjectCollection")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/work-streams-for-project/')) {
                return (
                    <span>
                        {renderIconOrImage("ReportLibrary")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/benefits-for-project/')) {
                return (
                    <span>
                        {renderIconOrImage("ReportLibrary")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/dependencies-for-project/')) {
                return (
                    <span>
                        {renderIconOrImage("ReportLibrary")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/milestones-for-work-stream/')) {
                return (
                    <span>
                        {renderIconOrImage("ReportLibrary")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/sign-off-for-project/')) {
                return (
                    <span>
                        {renderIconOrImage("PostUpdate")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/sign-off-for-portfolio/')) {
                return (
                    <span>
                        {renderIconOrImage("PostUpdate")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }
            else if (normalizedKey.startsWith('/a2/sign-off-for-programme/')) {
                return (
                    <span>
                        {renderIconOrImage("PostUpdate")}
                        <span style={commonStyle}>{link.name}</span>
                    </span>
                );
            }



            // Default behavior
            return <span>{link.name}</span>;

    }
}

